import { Formik, useFormikContext } from 'formik'
import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import Form from '../../Form'
import { MessageBar, MessageBarType } from '@fluentui/react'
import {
    FilePartyMetaDataDto,
    InvoiceAction,
    InvoiceDto,
    InvoiceMutationDto,
    InvoiceStatus,
    InvoiceTemplateDto,
    InvoiceType,
    MetaDataDto,
} from '../../../../config/finance/invoiceConfig'
import HeaderCard from './HeaderCard'
import { FormikConfig } from 'formik/dist/types'

import InvoiceFieldSets from './InvoiceFieldsets'
import FormContainer from '../../../../components/Form/FormContainer'
import InvoiceParties from './InvoiceParties'
import Loading from '../../../../components/Loading'
import { useDispatch, useSelector } from 'react-redux'
import { Store } from '../../../../reducers'
import { DataPromise } from '../../../../context/AxiosContext'
import useLineItemEntities, { LineItemEntities } from '../../../../hooks/finance/invoice/useLineItemEntities'
import useEntity from '../../../../hooks/useEntity'
import InvoiceFile from './InvoiceFile'
import { makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components'
import * as Yup from 'yup'
import usePersistInvoice, { PersistInvoice } from '../../../../hooks/finance/invoice/usePersistInvoice'
import { DocumentGenerationType } from '../../../../hooks/document/useDocumentGenerationHandler'
import useSubmitInvoiceDocumentGeneration, {
    SubmitInvoiceDocumentGeneration,
} from '../../../../hooks/finance/invoice/useSubmitInvoiceDocumentGeneration'
import useVatTypes from '../../../../hooks/useVatTypes'
import { useTranslation } from 'react-i18next'

type Props = {
    formikProps: FormikConfig<InvoiceMutationDto>
    invoice?: InvoiceDto
    templates: InvoiceTemplateDto[]
}

export type InvoiceProps = Omit<Props, 'formikProps'> & {
    meta: DataPromise<MetaDataDto>
    useInitialInvoice: boolean
    hasParties: boolean
    isEditable: boolean
    isFinal: boolean
    defaultVatType: string
    lineItemEntities: LineItemEntities
    lineItemGroupPrices: Map<string, PriceVat>
    persist: PersistInvoice
    generatePdf: SubmitInvoiceDocumentGeneration
}

export type PriceVat = {
    subtotal: number
    vat?: Vat[]
    total: number
}

export type Vat = {
    value: number
    type: string
}

const requiresType =
    () =>
    ([type], schema) => {
        return type && [InvoiceType.Split].includes(type) ? schema.nullable() : schema.required()
    }

const getValidationSchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        number: Yup.string().required(),
        debtorPivotId: Yup.string().label(translate('invoice.debtorpivotid')).nullable().when('type', requiresType()),
        clientPivotId: Yup.string().label(translate('invoice.clientpivotid')).nullable().when('type', requiresType()),
    })
}

export const getFilePartyFromMeta = (filePartyId: string, fileParties: FilePartyMetaDataDto[]): FilePartyMetaDataDto =>
    (fileParties || []).find(fileParty => fileParty.id === filePartyId)

const useStyles = makeStyles({
    container: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
})

function Container({ invoice, templates }: Props): ReactElement {
    const dispatch = useDispatch()
    const { t: translate } = useTranslation()
    const styles = useStyles()

    const { values, status, setStatus, isSubmitting, setFieldValue } = useFormikContext<InvoiceMutationDto>()
    // Use the initial invoice data instead of the metadata info when the file ID value is set to its original value.
    const useInitialInvoice = useMemo<boolean>(() => invoice && invoice?.file.id === values.fileId, [values.fileId])
    const meta = useSelector<Store, DataPromise<MetaDataDto>>(state => state.financeInvoice.meta)
    const { getVatType } = useVatTypes()
    const hasParties = useMemo<boolean>(() => meta.data?.fileParties.length > 0, [meta.data])
    const isEndUserInvoice = useMemo<boolean>(() => ![InvoiceType.Split].includes(values.type), [values.type])
    const isSplitInvoice = useMemo<boolean>(() => [InvoiceType.Split].includes(values.type), [values.type])

    const lineItemEntities = useLineItemEntities(meta, values)
    const persist = usePersistInvoice()
    const generatePdf = useSubmitInvoiceDocumentGeneration(DocumentGenerationType.Invoice)

    const lineItemGroupTriggers = useMemo(
        () =>
            JSON.stringify(
                values.lineItemGroups.map(lineItemGroup => ({
                    id: lineItemGroup.id,
                    precedence: lineItemGroup.precedence,
                    price: lineItemGroup.price,
                    vat: lineItemGroup.vatType,
                    entityIds: lineItemGroup.lineItems.map(lineItem => lineItem.entityId),
                })),
            ),
        [values.lineItemGroups],
    )
    const lineItemGroupPrices = useMemo<Map<string, PriceVat>>(
        () =>
            new Map(
                values.lineItemGroups.map(lineItemGroup => [
                    lineItemGroup.id,
                    lineItemEntities.getGroupPrice(
                        lineItemGroup,
                        getVatType(lineItemGroup.vatType || meta.data?.file.vatType),
                    ),
                ]),
            ),
        [lineItemGroupTriggers, meta.data],
    )

    const props: InvoiceProps = {
        invoice,
        templates,
        meta,
        hasParties,
        isEditable: [InvoiceStatus.New, InvoiceStatus.Draft].includes(values.status),
        isFinal: [InvoiceStatus.Final].includes(values.status),
        useInitialInvoice,
        lineItemEntities,
        lineItemGroupPrices,
        defaultVatType: meta.data?.file.vatType,
        persist,
        generatePdf,
    }

    useEffect(() => {
        if (isSplitInvoice) {
            setFieldValue('clientPivotId', undefined)
            setFieldValue('debtorPivotId', undefined)
            setFieldValue('recipientEmail', undefined)
            setFieldValue('recipientReference', undefined)
            setFieldValue('messageSubject', undefined)
            setFieldValue('messageBody', undefined)
            setFieldValue('paymentMessageBody', undefined)
            setFieldValue('children', [])
        } else {
            setFieldValue('children', [])
        }
    }, [values.type, isSplitInvoice])

    useEffect(() => {
        dispatch({ type: InvoiceAction.ResetMetaData })
        return () => {
            // Reset the metadata when unmounting this component.
            dispatch({ type: InvoiceAction.ResetMetaData })
        }
    }, [values.fileId])

    useEffect(() => {
        // Make sure to clear out any status messages after we're done.
        if (!isSubmitting) {
            setStatus(undefined)
        }
    }, [isSubmitting])

    return (
        <Form status={status} noLoading={status === undefined} className={mergeClasses(styles.container)}>
            {meta.loaded && !meta.loading && <HeaderCard {...props} />}
            <FormContainer fullWidth>
                <InvoiceFile {...props} />
                {meta.loading && <Loading label={translate('invoice.edit.loading.file')} />}
                {isEndUserInvoice && meta.data && <InvoiceParties {...props} />}
                {meta.data && <InvoiceFieldSets {...props} />}
            </FormContainer>
        </Form>
    )
}

export default function InvoiceForm(props: Omit<Props, 'templates'>): ReactElement {
    const { t: translate } = useTranslation()
    const [templates] = useEntity<InvoiceTemplateDto[]>(() => ({ url: '/finance/invoice/template' }), false)

    if (templates.loading) {
        return <Loading label={translate('invoice.edit.loading.templates')} />
    }
    return (
        <>
            {props.invoice?.credit && (
                <MessageBar messageBarType={MessageBarType.info}>
                    {translate('invoice.edit.info.credit')}
                    <Link to={`/finance/invoices/${props.invoice.credit.id}/edit`}>{props.invoice.credit.number}</Link>.
                </MessageBar>
            )}
            <Formik<InvoiceMutationDto>
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={getValidationSchema(translate)}
                {...props.formikProps}
            >
                <Container {...props} templates={templates.data} />
            </Formik>
        </>
    )
}
